import { useEffect, useMemo, useState, useCallback } from "react";
import { ComboBox } from "~/components/common/combo-box";
import { Checkbox } from "~/components/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { NodeTypeEnum } from "~/utils/api-util";
import { NodeTypeCell } from "~/utils/coldefs/node";
import type {
  FormOption,
  NodeMapDisplayAttribute,
  NodeMapSettingType,
} from "~/types/ui";
import type { NodeMapAttribute } from "~/types/api";
import DataTable from "~/components/common/table";

export function NodeMapSettingFormField({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (val: string) => void;
  options: FormOption[];
}) {
  const { t } = useTranslation();
  const [displayAttributes, setDisplayAttributes] = useState<
    NodeMapDisplayAttribute[]
  >([]);
  const [showTypes, setShowTypes] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [sliderValues, setSliderValues] = useState<number[]>([
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 24, 36, 48, 128,
  ]);

  useEffect(() => {
    try {
      const parsed = JSON.parse(value) as NodeMapSettingType;
      if (!parsed)
        throw new Error("Invalid JSON: parsed value is null or undefined");

      setDisplayAttributes(parsed.displayAttributes);
      setShowTypes(parsed.showTypes);
      setSliderValues(parsed.slidervalues);
    } catch (e) {
      // Invalid JSON
      setDisplayAttributes([]);
    }
  }, []);

  // sync serialized value whenever state changes
  useEffect(() => {
    onChange(
      JSON.stringify({
        displayAttributes,
        slidervalues: sliderValues,
        showTypes,
      } as NodeMapSettingType),
    );
  }, [displayAttributes, sliderValues, showTypes, onChange]);

  // Memoized callback functions to prevent re-renders
  const toggleDisplay = useCallback((nodeType: number) => {
    setShowTypes((prev) => {
      const idx = prev.findIndex((d) => d === nodeType);
      let newShowTypes: number[];
      if (idx === -1) {
        newShowTypes = [...prev, nodeType];
      } else {
        newShowTypes = prev.filter((d) => d !== nodeType);
      }

      return newShowTypes;
    });
  }, []);

  const updateAttribute = useCallback(
    (nodeType: number, attrNormalizedName: string | null) => {
      const attr = options.find((d) => d.value === attrNormalizedName);

      setDisplayAttributes((prev) => {
        let newDisplayAttributes = [...prev];
        const idx = newDisplayAttributes.findIndex(
          (d) => d.nodeType === nodeType,
        );
        let newAttribute: NodeMapAttribute | null = null;

        if (idx === -1) {
          newDisplayAttributes.push({
            nodeType,
            attribute: null,
          });
        }

        if (attr) {
          newAttribute = {
            name: attr.label,
            normalizedName: attr.value as string,
          };
        }

        // Fix the index issue - need to find index again after potential push
        const finalIdx = newDisplayAttributes.findIndex(
          (d) => d.nodeType === nodeType,
        );
        newDisplayAttributes[finalIdx].attribute = newAttribute;

        return newDisplayAttributes;
      });
    },
    [options],
  );

  // Memoize node types calculation
  const nodeTypes = useMemo(() => {
    const ignoreTypes: number[] = [];
    return Object.values(NodeTypeEnum).filter((v) => !ignoreTypes.includes(v));
  }, []);

  // Create the data for the table - memoized with proper dependencies
  const data = useMemo(
    () =>
      nodeTypes.map((nt) => ({
        nodeType: nt,
        display: showTypes.includes(nt),
        attribute:
          displayAttributes.find((d) => d.nodeType === nt)?.attribute
            ?.normalizedName || null,
      })),
    [displayAttributes, showTypes, nodeTypes],
  );

  // Memoized column definitions with stable callbacks
  const colDef: ColumnDef<any>[] = useMemo(
    () => [
      {
        id: "nodeType",
        accessorKey: "nodeType",
        header: t("nodes.headers.nodeType"),
        cell: (info) => <NodeTypeCell nodeType={info.getValue() as number} />,
      },
      {
        id: "display",
        accessorKey: "display",
        header: t("common.visible"),
        cell: (info) => (
          <Checkbox
            checked={info.getValue() as boolean}
            onCheckedChange={() => {
              toggleDisplay(info.row.original.nodeType);
            }}
          />
        ),
      },
      {
        id: "attribute",
        accessorKey: "attribute",
        header: t("nodes.attributes.headers.name"),
        cell: (info) => (
          <ComboBox
            clearable
            options={options}
            value={info.row.original.attribute}
            onChange={(v) => {
              updateAttribute(info.row.original.nodeType, v);
            }}
          />
        ),
      },
    ],
    [t, options, toggleDisplay, updateAttribute], // stable dependencies
  );

  return (
    <div>
      <DataTable columns={colDef} data={data} />
    </div>
  );
}
